import React, { useEffect, useMemo } from "react";
import { ScreenContext } from "@foundation/screen";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import {
  TickerChart,
  type TickerChartData,
  type TickerChartDataPoint,
} from "@bloomberg/media-ui-markets";
import { Refresh } from "@bloomberg/media-ui-icons";
import { Placeholder } from "@bloomberg/media-ui-article";
import styles from "./styles.module.css";
import ComparisonChartTabs from "./ComparisonChartTabs";
import ComparisonChartLegend from "./ComparisonChartLegend";
import {
  useChartRange,
  useComparisonChartError,
  useComparisonChartLoading,
  useComparisonData,
  useSelectedPortfolioId,
  useSelectedLegends,
  useCurrentWatchlist,
  WatchlistContext,
} from "~context/WatchlistContext";
import { LOADING_STATE, WATCHLIST_FAILURE_TEXT } from "~constants/Portfolio";
import Notification from "~components/Notification";
import { getChartTickerColor } from "~utils/portfolio/getChartColors";

function ChartLoadingState() {
  return (
    <div className="w-full">
      <Placeholder>
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
      </Placeholder>
    </div>
  );
}

type ComparisonChartProps = {
  readonly handleChartInteraction?: (
    chartDataPoint: TickerChartDataPoint
  ) => void;
  readonly isMaximized: boolean;
  readonly children?: React.ReactNode;
};

export default function ComparisonChart(props: ComparisonChartProps) {
  const { handleChartInteraction, isMaximized, children } = props;

  const comparisonData = useComparisonData();
  const currentWatchlist = useCurrentWatchlist();
  const selectedTab = useChartRange();
  const isLoading = useComparisonChartLoading();
  const error = useComparisonChartError();
  const selectedLegends = useSelectedLegends();

  const { breakpoints } = React.useContext(ScreenContext);
  const { tablet, smallDesktop, extraSmallDesktop, largeDesktop } = breakpoints;

  // Filter chart data in component using useMemo (NOT a derived selector to avoid infinite loop)
  const chartData = useMemo(() => {
    if (!comparisonData?.chartData) return [];

    const rawChartData = comparisonData.chartData;

    // If no legends selected, show all
    if (selectedLegends.size === 0) return rawChartData;

    // Filter to selected legends
    return rawChartData.filter((ticker) => selectedLegends.has(ticker.id));
  }, [comparisonData, selectedLegends]);

  // Fetch comparison chart on mount and when watchlist changes
  const store = React.useContext(WatchlistContext);

  useEffect(() => {
    // Guard: Only fetch when a watchlist is fully loaded
    // Prevents premature requests before session/auth is ready
    // When switching watchlists, this effect re-runs when the new watchlist data is loaded
    if (!currentWatchlist?.id || !store) return;

    const { fetchComparisonChart } = store.getState();

    void fetchComparisonChart();
  }, [currentWatchlist?.id, store]);

  // Add colors to chart data
  const chartDataWithColors = useMemo(() => {
    if (!chartData?.length) return [];

    return chartData.map((ticker, index) => ({
      ...ticker,
      color: getChartTickerColor(index),
    }));
  }, [chartData]);

  const handleMouseLeave = () => {
    trackClickEvent(
      "chartHover",
      "hover_comparison_chart",
      "uiaction",
      "Market Data"
    );
  };

  // Calculate x-axis interval based on chart date range
  const xAxisInterval = useMemo(() => {
    if (selectedTab === "5Y") {
      return "month";
    }

    return selectedTab === "1D" ? "hour" : "day";
  }, [selectedTab]);

  // Get chart dimensions based on breakpoint
  const { chartWidth, chartHeight } = useMemo(() => {
    if (isMaximized) {
      if (largeDesktop) {
        return { chartWidth: 1010, chartHeight: 578 };
      }
      if (smallDesktop) {
        return { chartWidth: 797, chartHeight: 456 };
      }
      if (tablet || extraSmallDesktop) {
        return { chartWidth: 617, chartHeight: 299 };
      }

      return { chartWidth: 500, chartHeight: 300 };
    }

    // Minimized state
    if (largeDesktop || smallDesktop) {
      return { chartWidth: 402, chartHeight: 200 };
    }

    if (tablet || extraSmallDesktop) {
      return { chartWidth: 617, chartHeight: 299 };
    }

    return { chartWidth: 500, chartHeight: 300 };
  }, [tablet, extraSmallDesktop, smallDesktop, largeDesktop, isMaximized]);

  const refreshChart = () => {
    if (store) {
      const { fetchComparisonChart } = store.getState();

      void fetchComparisonChart();
    }
  };

  if (isLoading) {
    return (
      <div className={styles.comparisonChartContainer}>
        <ChartLoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.comparisonChartContainer}>
        <Notification
          text={error || WATCHLIST_FAILURE_TEXT}
          variant="lineBreak"
          title=""
          actionIcon={<Refresh />}
          actionText="Refresh"
          onActionClick={refreshChart}
        />
      </div>
    );
  }

  if (!comparisonData?.chartData?.length) {
    return (
      <div className={styles.comparisonChartContainer}>
        <div className={styles.emptyState}>
          <p>No comparison data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.comparisonChartContainer}>
      <ComparisonChartTabs />
      {/* Render children (legend for minimized state) */}
      {children}
      <div onMouseLeave={handleMouseLeave}>
        <TickerChart
          tickerSnapshots={chartDataWithColors as Array<TickerChartData>}
          renderMode={chartDataWithColors.length === 1 ? "price" : "percentage"}
          showGradient={chartDataWithColors.length === 1}
          pricePrecision={2}
          xAxisInterval={xAxisInterval}
          enableDragInteraction={isMaximized}
          handleChartHoverInteraction={handleChartInteraction}
          width={chartWidth}
          height={chartHeight}
        />
      </div>
    </div>
  );
}
